/**
 * BatchDetailDrawer Component
 *
 * User Story: US-P6-001 - Inventory Management
 * Step 3: Batch Receipt (Shipment Entry)
 *
 * Slide-out drawer showing batch details and usage history.
 * Allows editing of notes and invoice reference.
 */

import { useState, useEffect } from 'react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Package,
  Calendar,
  Building2,
  FileText,
  History,
  Loader2,
  Pencil,
  Check,
  X,
  Receipt,
  Truck,
} from 'lucide-react'
import BatchStatusBadge from './BatchStatusBadge'
import { useInventoryBatch } from '@/hooks/useInventoryBatches'
import { parseDateString } from '../../utils/dateUtils'

/**
 * Format a date for display
 */
function formatDate(dateString) {
  if (!dateString) return '-'
  return parseDateString(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * Format a number as currency (GBP)
 */
function formatCurrency(value) {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Format a number with appropriate precision
 */
function formatNumber(value, decimals = 2) {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(value)
}

/**
 * Calculate percentage remaining
 */
function calculatePercentage(remaining, received) {
  if (!received || received === 0) return 0
  return Math.round((remaining / received) * 100)
}

/**
 * Info row component for consistent layout
 */
function InfoRow({ icon: Icon, label, value, className }) {
  return (
    <div className={`flex items-start gap-3 ${className || ''}`}>
      {Icon && <Icon className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="font-medium">{value}</p>
      </div>
    </div>
  )
}

/**
 * BatchDetailDrawer Component
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the drawer is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {string} props.batchId - ID of the batch to display
 * @param {Function} props.onUpdate - Callback when batch is updated
 * @param {boolean} props.isUpdating - Whether an update is in progress
 */
export default function BatchDetailDrawer({
  open,
  onOpenChange,
  batchId,
  onUpdate,
  isUpdating = false,
}) {
  const { batch, isLoading, error, refetch } = useInventoryBatch(open ? batchId : null)

  // Edit state
  const [isEditing, setIsEditing] = useState(false)
  const [editData, setEditData] = useState({
    invoice_reference: '',
    notes: '',
  })

  // Reset edit state when batch changes
  useEffect(() => {
    if (batch) {
      setEditData({
        invoice_reference: batch.invoice_reference || '',
        notes: batch.notes || '',
      })
      setIsEditing(false)
    }
  }, [batch])

  // Handle edit save
  const handleSave = async () => {
    if (!onUpdate) return

    try {
      await onUpdate(batchId, editData)
      setIsEditing(false)
      refetch()
    } catch (err) {
      console.error('Failed to update batch:', err)
    }
  }

  // Handle edit cancel
  const handleCancel = () => {
    setEditData({
      invoice_reference: batch?.invoice_reference || '',
      notes: batch?.notes || '',
    })
    setIsEditing(false)
  }

  const percentage = batch
    ? calculatePercentage(batch.quantity_remaining, batch.quantity_received)
    : 0
  const unit = batch?.item?.unit_of_measure || ''

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Batch Details
          </SheetTitle>
          <SheetDescription>
            {batch?.batch_code || 'Loading...'}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="text-center text-destructive">
              <p>Failed to load batch details</p>
              <p className="text-sm">{error}</p>
            </div>
          ) : batch ? (
            <>
              {/* Batch Info Card */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Batch Information</CardTitle>
                    <BatchStatusBadge status={batch.status} showIcon />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <InfoRow
                      icon={Package}
                      label="Item"
                      value={batch.item?.name || '-'}
                    />
                    <InfoRow
                      icon={Building2}
                      label="Supplier"
                      value={batch.supplier?.name || 'Not specified'}
                    />
                    <InfoRow
                      icon={Calendar}
                      label="Purchase Date"
                      value={formatDate(batch.purchase_date)}
                    />
                    <InfoRow
                      label="Batch Code"
                      value={
                        <span className="font-mono text-sm">{batch.batch_code}</span>
                      }
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Stock Status Card */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Stock Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Remaining</span>
                      <span className="font-medium">
                        {formatNumber(batch.quantity_remaining)} / {formatNumber(batch.quantity_received)} {unit}
                      </span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                    <p className="text-xs text-muted-foreground text-right">
                      {percentage}% remaining
                    </p>
                  </div>

                  <Separator />

                  {/* Cost Breakdown */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Product Cost</span>
                      <span>{formatCurrency(batch.product_cost)}</span>
                    </div>
                    {batch.delivery_cost > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Truck className="h-3 w-3" /> Delivery Cost
                        </span>
                        <span>{formatCurrency(batch.delivery_cost)}</span>
                      </div>
                    )}
                  </div>

                  <Separator />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Landed Cost</p>
                      <p className="text-lg font-bold">{formatCurrency(batch.total_cost)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Unit Cost</p>
                      <p className="text-lg font-bold">
                        {formatCurrency(batch.unit_cost)}/{unit}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Editable Fields */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Receipt className="h-4 w-4" />
                      Reference & Notes
                    </CardTitle>
                    {!isEditing && onUpdate && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsEditing(true)}
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isEditing ? (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="invoice_ref">Invoice Reference</Label>
                        <Input
                          id="invoice_ref"
                          value={editData.invoice_reference}
                          onChange={(e) => setEditData(prev => ({
                            ...prev,
                            invoice_reference: e.target.value
                          }))}
                          placeholder="e.g., INV-2024-001"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="notes">Notes</Label>
                        <Textarea
                          id="notes"
                          value={editData.notes}
                          onChange={(e) => setEditData(prev => ({
                            ...prev,
                            notes: e.target.value
                          }))}
                          placeholder="Additional notes..."
                          rows={3}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleSave}
                          disabled={isUpdating}
                        >
                          {isUpdating ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4 mr-1" />
                          )}
                          Save
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCancel}
                          disabled={isUpdating}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <InfoRow
                        icon={FileText}
                        label="Invoice Reference"
                        value={batch.invoice_reference || 'Not specified'}
                      />
                      <InfoRow
                        label="Notes"
                        value={batch.notes || 'No notes'}
                        className={!batch.notes && 'text-muted-foreground'}
                      />
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Usage History */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <History className="h-4 w-4" />
                    Usage History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {batch.usage_history?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Production Batch</TableHead>
                          <TableHead className="text-right">Quantity</TableHead>
                          <TableHead className="text-right">Cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {batch.usage_history.map((usage) => (
                          <TableRow key={usage.id}>
                            <TableCell className="text-muted-foreground">
                              {formatDate(usage.usage_date)}
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {usage.substrate_batch?.batch_code || '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatNumber(usage.quantity)} {unit}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(usage.total_cost)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No usage recorded yet
                    </p>
                  )}
                </CardContent>
              </Card>
            </>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  )
}
